import { createAPIRequest, createAPIResponse, ResponseWrapper } from '@tlx/astro-shared';
import { useCallback } from 'react';
import useSWR from 'swr';

type PageOptionsDTO<TOptions> = {
    data: TOptions;
};

export type SalaryOnboardingPageOptions = {
    onboardingCompleted: boolean;
    onboardingDismissed: boolean;
    currentStep: SalaryOnboardingStep;
    completedSteps: SalaryOnboardingStep[];
    skippedSteps: SalaryOnboardingStep[];
};

export type SalaryOnboardingStep =
    | 'WELCOME'
    | 'PAYMENT_OF_SALARY'
    | 'PENSION'
    | 'VACATION_PAY_AND_RATE'
    | 'NORMAL_TIME'
    | 'ACTIVATE_FUNCTIONS'
    | 'ALTINN'
    | 'TIPS';

export type SalaryOnboardingOptionsReturn = ReturnType<typeof useSalaryOnboardingPageOptions>;
export function useSalaryOnboardingPageOptions() {
    const { data, error, isLoading, mutate } = useSWR<ResponseWrapper<PageOptionsDTO<SalaryOnboardingPageOptions>>>(
        getSalaryOnboardingPageOptionsURL(),
        {
            revalidateIfStale: false,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
        },
    );

    const options = data?.value.data;

    const setOnboardingCompleted = useCallback(async () => {
        if (!options) {
            return;
        }

        const postData = {
            ...options,
            onboardingCompleted: true,
        };
        mutate(postSalaryOnboardingPageOptions(postData), {
            optimisticData: {
                value: { data: postData },
            },
        });
    }, [options, mutate]);

    const setStepCompleted = useCallback(
        async (step: SalaryOnboardingStep) => {
            if (!options) {
                return;
            }

            const optionIsCompleted = options.completedSteps.includes(step);
            if (optionIsCompleted) {
                return;
            }

            const postData = {
                ...options,
                completedSteps: [...options.completedSteps, step],
            };
            mutate(postSalaryOnboardingPageOptions(postData), {
                optimisticData: { value: { data: postData } },
            });
        },
        [options, mutate],
    );

    const setStepIncomplete = useCallback(
        async (step: SalaryOnboardingStep) => {
            if (!options) {
                return;
            }

            const postData = {
                ...options,
                completedSteps: options.completedSteps.filter((stepId) => stepId !== step),
            };
            mutate(postSalaryOnboardingPageOptions(postData), {
                optimisticData: { value: { data: postData } },
            });
        },
        [options, mutate],
    );

    return {
        data: data?.value.data,
        error,
        isLoading,
        setOnboardingCompleted,
        setStepCompleted,
        setStepIncomplete,
    };
}

const SALARY_ONBOARDING_PAGE_OPTIONS_URL = `/v2/options/page/byKey`;
const SALARY_ONBOARDING_PAGE_OPTIONS_KEY = 'SalaryOnboardingOptions';
const SALARY_ONBOARDING_PAGE_OPTIONS_TYPE = 'SalaryOnboardingOptions';

function getSalaryOnboardingPageOptionsURL() {
    const defaultFields = ['data'];
    const params = new URLSearchParams();
    params.set('key', SALARY_ONBOARDING_PAGE_OPTIONS_KEY);
    params.set('type', SALARY_ONBOARDING_PAGE_OPTIONS_TYPE);
    params.set('level', 'Company');
    params.set('fields', [...defaultFields].sort().join(','));

    return `${SALARY_ONBOARDING_PAGE_OPTIONS_URL}?${params.toString()}`;
}

async function postSalaryOnboardingPageOptions(options: SalaryOnboardingPageOptions) {
    const request = createAPIRequest(getSalaryOnboardingPageOptionsURL(), {
        method: 'POST',
        body: JSON.stringify(options),
        headers: {
            'content-type': 'application/json; charset=utf-8',
        },
    });
    const response = await window.fetch(request);
    return await createAPIResponse<ResponseWrapper<PageOptionsDTO<SalaryOnboardingPageOptions>>>(request, response);
}
